import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, Loader2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Field, inputCls, btnPrimary } from "@/components/Form";

const EMPTY = { code: "", credits: 10, max_uses: 0, valid_days: 0 };

function PromoForm({ onDone }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/promos", { code: f.code, credits: Number(f.credits), max_uses: Number(f.max_uses), valid_days: Number(f.valid_days) });
      toast.success(`Código ${data.code} criado`); setF(EMPTY); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[1fr_120px_120px_120px_auto] sm:items-end" data-testid="promo-form">
      <Field label="Código (vazio = automático)"><input data-testid="promo-code-input" placeholder="Ex.: GANHE10" className={`${inputCls} uppercase`} value={f.code} onChange={set("code")} /></Field>
      <Field label="Contestações"><input data-testid="promo-credits-input" type="number" min={1} max={1000} required className={inputCls} value={f.credits} onChange={set("credits")} /></Field>
      <Field label="Máx. usos"><input data-testid="promo-maxuses-input" type="number" min={0} title="0 = ilimitado" className={inputCls} value={f.max_uses} onChange={set("max_uses")} /></Field>
      <Field label="Validade (dias)"><input data-testid="promo-validdays-input" type="number" min={0} title="0 = sem validade" className={inputCls} value={f.valid_days} onChange={set("valid_days")} /></Field>
      <button data-testid="promo-create-btn" disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Criar</button>
    </form>
  );
}

function PromoRow({ p, onToggle }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`promo-row-${p.promo_id}`}>
      <td className="px-4 py-3"><button onClick={() => navigator.clipboard.writeText(p.code).then(() => toast.success("Copiado"))} className="inline-flex items-center gap-1.5 font-mono text-sm font-bold hover:text-amber-700">{p.code}<Copy className="h-3.5 w-3.5" /></button></td>
      <td className="px-4 py-3 text-sm">+{p.credits}</td>
      <td className="px-4 py-3 text-sm" data-testid={`promo-uses-${p.promo_id}`}>{p.uses}{p.max_uses ? ` / ${p.max_uses}` : " / ∞"}</td>
      <td className="px-4 py-3 text-sm">{p.expires_at ? fmtDate(p.expires_at) : "Sem validade"}</td>
      <td className="px-4 py-3">
        <button data-testid={`promo-toggle-${p.promo_id}`} onClick={() => onToggle(p)} className={`rounded-full px-3 py-1 text-xs font-semibold ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Ativo" : "Inativo"}</button>
      </td>
    </tr>
  );
}

export default function PromosPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/promos").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (p) => { try { await api.post(`/admin/promos/${p.promo_id}/toggle`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Cada cliente pode usar um código promocional uma vez. As contestações entram no saldo de bônus dele. Em "Máx. usos" e "Validade", 0 significa ilimitado.</p>
      <PromoForm onDone={load} />
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="promos-table">
        <table className="w-full min-w-[640px] text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Código", "Bônus", "Usos", "Válido até", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{items.map((p) => <PromoRow key={p.promo_id} p={p} onToggle={toggle} />)}</tbody>
        </table>
        {!items.length && <p className="p-6 text-center text-sm text-slate-500">Nenhum código promocional ainda.</p>}
      </div>
    </div>
  );
}
